// Cliente em foco viajando pela QUERY da URL — o jeito de abrir um módulo em
// NOVA ABA já filtrado por um cliente (14/09/2026, lateral do chat imersivo).
//
// Uma aba nova é um processo de browser à parte, com Vuex vazio: o
// `navigation/enterClient` despachado na aba de ORIGEM não a alcança, e o
// contexto (gestão ↔ cliente) é por aba (`context-storage.js`, sessionStorage).
// Rotas agregadas (CRM, Compliance, Consolidador, Tarefas, Transcrições) não
// carregam cliente no param — daí a query: `?cliente_em_foco=<id>` é lida pelo
// `beforeEach` do router ANTES de `applyClienteHiddenRouteGuard`, que então
// redireciona a rota agregada para a entrada da visão de cliente sozinho.
//
// ★ [MUST-1, review do PR #2261] O nome NÃO pode ser `cliente` cru: o módulo
// `ws` já usa exatamente essa query (`ws/views/view-clients.vue` →
// `ws.operations?cliente=<id>`) para um filtro LOCAL da própria tela — nada a
// ver com o contexto global de navegação. Com o mesmo nome, clicar num
// contador de operações do WS disparava `navigation/enterClient` e tirava o
// consultor da visão de Gestão sem ele ter pedido isso. `cliente_em_foco` é
// exclusivo desta feature.
import { resolverClientePorId } from '@utils/cliente-por-id/cliente-por-id'

export const QUERY_CLIENTE = 'cliente_em_foco'

/**
 * Monta a rota com o cliente na query.
 *   rotaComClienteNaQuery({ name: 'crm.overview', cliente: { id: 124 } })
 *   // → { name: 'crm.overview', query: { cliente: '124' } }
 */
export function rotaComClienteNaQuery({ name, cliente }) {
  const rota = { name }
  const id = cliente && cliente.id
  if (id) rota.query = { [QUERY_CLIENTE]: String(id) }
  return rota
}

/**
 * Entra no cliente da query, se houver e se a ABA ainda não estiver nesse
 * cliente. Nunca redireciona — só prepara o contexto; falha em silêncio
 * quando o id não resolve (o guard de rota escondida decide o resto).
 * Cliente final nunca entra em "visão por cliente" (é conceito de
 * profissional).
 *
 * ★ [MUST-2, review do PR #2261] O curto-circuito exige `navigation/
 * isClienteContext`, não só `atual.id === id`. `user/currentClient` é
 * restaurado do `localStorage` no BOOT de toda aba nova (o último cliente
 * usado, `restoreSearchClients`/`addSearchClient`), enquanto o contexto
 * (gestão ↔ cliente) é POR ABA (`sessionStorage`) e nasce em GESTÃO. Sem essa
 * exigência, a segunda aba aberta para o MESMO cliente (que é justamente o
 * último usado) via o link "id igual ao já salvo" e nunca despachava
 * `enterClient` — a aba ficava presa em Gestão com uma rota agregada que
 * deveria ter sido redirecionada para a visão do cliente (reproduzido no QA
 * de navegador: 2ª aba com o mesmo cliente ficou com `isClienteContext:
 * false`).
 */
export async function applyClienteDaQueryGuard(routeTo, store) {
  const id = routeTo && routeTo.query && routeTo.query[QUERY_CLIENTE]
  if (!id) return false
  const user = store.getters['user/currentUser'] || {}
  if (user.IS_CLIENTE) return false
  const atual = store.getters['user/currentClient']
  const jaEstaNoContextoDesteCliente =
    Boolean(store.getters['navigation/isClienteContext']) &&
    atual &&
    String(atual.id) === String(id)
  if (jaEstaNoContextoDesteCliente) return false
  const cliente = await resolverClientePorId(store, id)
  if (!cliente) return false
  store.dispatch('navigation/enterClient', cliente)
  return true
}
